/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { If, Then, Else, When } from "react-if";
import {
  Col,
  ExtendedTooltip,
  FlexContainer,
  Row,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { Box } from "~/components";
import { dateFormat, NO_FORECAST_TEXT } from "~/constants";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import { formatMoney } from "~/tools";
import {
  CostWrap,
  IconBox,
} from "~/views/environments/components/BudgetDetails";

type Props = {
  currentMonthForecastValue: number;
  currentMonthValue: number;
  previousMonthValue: number;
};

export function AccountSpends({
  currentMonthForecastValue,
  currentMonthValue,
  previousMonthValue,
}: Props) {
  const { currency } = useCurrency();

  const showForecastUp = currentMonthForecastValue >= previousMonthValue;
  const isValidForecast = currentMonthForecastValue >= 0;

  return (
    <Box
      color={theme.color.background.ui03}
      border={theme.color.border.border01}
    >
      <div
        css={{
          padding: theme.spacing.spacing04,
          borderBottom: `1px solid ${theme.color.border.border01}`,
        }}
      >
        <Row>
          <Col>
            <Text isTitle size="sm" tag="span">
              Cost
            </Text>
          </Col>
          <Col css={{ textAlign: "right" }}>
            <Text isTitle size="sm" tag="span">
              {periodDayJs().format(dateFormat.monthYear)}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <FlexContainer justifyContent="flex-start" alignItems="center">
              <Text size="xl" weight="medium" tag="div">
                {formatMoney(currentMonthValue, currency)}
              </Text>
            </FlexContainer>
          </Col>
        </Row>
      </div>
      <Col>
        <Box
          radius="0"
          padding={`${theme.spacing.spacing02} ${theme.spacing.spacing04}}`}
        >
          <Row>
            <Col sm={4}>
              <Row>
                <When condition={isValidForecast}>
                  <IconBox>
                    <SVGIcon
                      name={showForecastUp ? "forecastUp" : "forecastDown"}
                      color="danger"
                    />
                  </IconBox>
                </When>
                <Box>
                  <CostWrap>
                    <Text isTitle size="xs" tag="span">
                      Monthly Forecast
                    </Text>
                    <If condition={isValidForecast}>
                      <Then>
                        <Text tag="div" weight="medium">
                          {formatMoney(currentMonthForecastValue, currency)}
                        </Text>
                      </Then>
                      <Else>
                        <ExtendedTooltip caption={NO_FORECAST_TEXT}>
                          <SVGIcon name="minus" size="sm" />
                        </ExtendedTooltip>
                      </Else>
                    </If>
                  </CostWrap>
                </Box>
              </Row>
            </Col>
            <Col sm={4}>
              <Box>
                <CostWrap>
                  <Text isTitle size="xs" tag="span">
                    Previous Month
                  </Text>
                  <Text tag="div" weight="medium">
                    {formatMoney(previousMonthValue, currency)}
                  </Text>
                </CostWrap>
              </Box>
            </Col>
          </Row>
        </Box>
      </Col>
    </Box>
  );
}
